import { NearbyCheckIn } from '../lib/supabase';
import { Flame, MapPin, Calendar, Star, Navigation } from 'lucide-react';

interface MapMarkerCardProps {
  checkIn: NearbyCheckIn;
  onViewCheckIn: (checkInId: string) => void;
}

export default function MapMarkerCard({ checkIn, onViewCheckIn }: MapMarkerCardProps) {
  const {
    username,
    tobacco_brand,
    tobacco_blend,
    tobacco_manual,
    pipe_maker,
    pipe_shape,
    pipe_manual,
    location_name,
    outdoor_seating,
    covered_seating,
    heaters,
    photo_url,
    created_at
  } = checkIn;

  // Build amenities string
  const amenities = [];
  if (outdoor_seating) amenities.push('🌳');
  if (covered_seating) amenities.push('☂️');
  if (heaters) amenities.push('🔥');

  // Format date
  const formattedDate = new Date(created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });

  // Get tobacco display
  const tobaccoDisplay = tobacco_brand && tobacco_blend 
    ? `${tobacco_brand} - ${tobacco_blend}`
    : tobacco_manual || 'Unknown Tobacco';

  // Get pipe display
  const pipeDisplay = pipe_maker && pipe_shape
    ? `${pipe_maker} ${pipe_shape}`
    : pipe_manual || null;

  return (
    <div style={{
      background: 'rgba(11, 11, 11, 0.95)',
      backdropFilter: 'blur(10px)',
      border: '1px solid var(--color-accent)',
      borderRadius: '12px',
      padding: '1rem',
      minWidth: '280px',
      maxWidth: '320px',
      color: 'white',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
    }}>
      {/* Header with user info */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '0.75rem'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: 'var(--color-accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#0b0b0b',
          fontWeight: '600',
          fontSize: '0.875rem'
        }}>
          {username.charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>
            {checkIn.is_profile_public !== false ? username : 'Anonymous'}
          </div>
          <div style={{ 
            fontSize: '0.75rem', 
            color: '#999',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}>
            <Calendar size={12} />
            {formattedDate}
          </div>
        </div>
      </div>

      {/* Location */}
      {location_name && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem',
          fontSize: '0.875rem',
          color: 'var(--color-accent)'
        }}>
          <MapPin size={14} />
          {location_name}
        </div>
      )}

      {/* Photo thumbnail */}
      {photo_url && (
        <div style={{
          marginBottom: '0.75rem',
          borderRadius: '8px',
          overflow: 'hidden',
          height: '120px',
          backgroundImage: `url(${photo_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />
      )}

      {/* Tobacco info */}
      <div style={{ marginBottom: '0.5rem' }}>
        <div style={{
          fontSize: '0.875rem',
          fontWeight: '600',
          marginBottom: '0.25rem'
        }}>
          {tobaccoDisplay}
        </div>
        
        {/* Pipe info */}
        {pipeDisplay && (
          <div style={{
            fontSize: '0.8rem',
            color: '#d3de5e',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}>
            <Flame size={12} />
            {pipeDisplay}
          </div>
        )}
      </div>

      {/* Amenities */}
      {amenities.length > 0 && (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '0.75rem',
          fontSize: '0.875rem'
        }}>
          {amenities.join(' ')}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={() => onViewCheckIn(checkIn.id)}
          style={{
            flex: 1,
            padding: '0.75rem',
            backgroundColor: 'var(--color-accent)',
            color: '#0b0b0b',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#e6ff00';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent)';
          }}
        >
          View Check-in
        </button>
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${checkIn.latitude},${checkIn.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1,
            padding: '0.75rem',
            backgroundColor: 'var(--color-card)',
            border: '1px solid var(--color-accent)',
            borderRadius: '8px',
            color: 'var(--color-accent)',
            textDecoration: 'none',
            fontSize: '0.875rem',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.35rem'
          }}
        >
          <Navigation size={14} /> Directions
        </a>
      </div>
    </div>
  );
}
